import { X } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';

/** How far a sheet must be dragged down before letting go closes it. */
const DISMISS_PX = 80;

/**
 * A sheet that rises from the bottom of a phone screen.
 *
 * The phone's answer to a popover: a menu anchored to a 36px pill is a target
 * a thumb misses, and a centred dialog puts its choices as far from the thumb
 * as the screen allows. A sheet puts them under it.
 *
 * It closes the four ways a phone user expects — the X, a tap on the dimmed
 * page, a swipe down on its handle, and Escape for a keyboard — and hands
 * focus back to whatever opened it, so a screen reader is not left at the top
 * of the page. The page behind does not scroll while it is open.
 */
export function PhoneSheet({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const panel = useRef<HTMLDivElement | null>(null);
  // The latest close, read by listeners bound once: re-binding on every
  // render would refocus the sheet and flicker the page's scroll lock.
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);

  const [drag, setDrag] = useState(0);
  const dragFrom = useRef<number | null>(null);

  useEffect(() => {
    const opener = document.activeElement;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeRef.current();
    };
    window.addEventListener('keydown', onKey);
    panel.current?.focus();
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      if (opener instanceof HTMLElement) opener.focus();
    };
  }, []);

  const endDrag = () => {
    if (dragFrom.current === null) return;
    dragFrom.current = null;
    if (drag >= DISMISS_PX) closeRef.current();
    else setDrag(0);
  };

  return (
    <div
      className="dbp-sheet-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panel}
        className={drag ? 'dbp-sheet dragging' : 'dbp-sheet'}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        style={drag ? { transform: `translateY(${drag}px)` } : undefined}
      >
        {/* The handle and the title row are the drag surface; the body keeps
            its own scroll, or a list could never be scrolled back up. */}
        <div
          className="dbp-sheet-head"
          onPointerDown={(event) => {
            // The X is in this row, and a drag begun on it would capture the
            // pointer and swallow the tap that was meant to close the sheet.
            if ((event.target as HTMLElement).closest('button')) return;
            dragFrom.current = event.clientY;
            // Keep the drag once the finger slides off the handle, which on a
            // 40px strip is almost immediately.
            try {
              event.currentTarget.setPointerCapture(event.pointerId);
            } catch {
              // No capture (an older engine, a synthetic event): the drag
              // still works while the finger stays on the handle.
            }
          }}
          onPointerMove={(event) => {
            if (dragFrom.current === null) return;
            setDrag(Math.max(0, event.clientY - dragFrom.current));
          }}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          <span className="dbp-grabber" aria-hidden="true" />
          <h2>{title}</h2>
          <button type="button" className="dbp-icon-btn" aria-label="Close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        <div className="dbp-sheet-body">{children}</div>
      </div>
    </div>
  );
}
